import React, { useState, useContext } from 'react';
import {
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Chip,
  CircularProgress,
  IconButton,
  TextField,
  InputAdornment
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { Description, Download, ExpandLess as ExpandLessIcon, ExpandMore as ExpandMoreIcon, Search } from '@mui/icons-material';
import { ContextSession } from "../../services/ContextSession";

const HistorialAvances = ({
  avances = [], // Valor por defecto
  loadingAvances,
  showAdjuntos,
  setShowAdjuntos,
  getAdjuntosRelacionados = () => [], // Valor por defecto
  handlePreview = () => {},
  handleDescargarAdjunto = () => {},
  descargandoId
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { sessionData } = useContext(ContextSession);

  // Validar que avances sea un array
  const avancesValidos = Array.isArray(avances) ? avances : [];

  // Filtrar avances basados en el término de búsqueda (CON VALIDACIÓN)
  const filteredAvances = avancesValidos
    .filter(avance => {
      if (!avance) return false;
      
      const descripcion = avance.descripcionavance || '';
      const fecha = avance.fechaavance ? new Date(avance.fechaavance).toLocaleString('es-HN') : '';
      const usuarioId = avance.usuarioid ? avance.usuarioid.toString() : '';
      
      // Validación segura para toLowerCase()
      const descripcionLower = typeof descripcion === 'string' ? descripcion.toLowerCase() : '';
      const fechaLower = typeof fecha === 'string' ? fecha.toLowerCase() : '';
      const searchTermLower = typeof searchTerm === 'string' ? searchTerm.toLowerCase() : '';
      
      return descripcionLower.includes(searchTermLower) ||
             fechaLower.includes(searchTermLower) ||
             usuarioId.includes(searchTerm);
    })
    .sort((a, b) => {
      const fechaA = a.fechaavance ? new Date(a.fechaavance) : new Date(0);
      const fechaB = b.fechaavance ? new Date(b.fechaavance) : new Date(0);
      return fechaB - fechaA;
    });

  // Función para obtener el nombre del usuario con validación
  const getNombreUsuario = (avance) => {
  if (!avance) return 'Usuario no disponible';
  
  // Si el avance ya tiene el nombre del usuario, usamos ese
  if (avance.usuarionombre) {
    return avance.usuarionombre;
  }
  
  // Si el usuario del avance es el mismo que el de la sesión actual
  if (avance.usuarioid === sessionData?.id) {
    return `${sessionData?.nombre || ''} ${sessionData?.apellido || ''}`.trim() || 'Usuario actual';
  }
  
  // Si no tenemos información, mostramos el ID
  return `ID: ${avance.usuarioid || 'N/A'}`;
};

  return (
    <Paper elevation={2} style={{ marginTop: '2rem', padding: '2rem', maxHeight: '80vh', backgroundColor: '#f0f0f0', display: 'flex', flexDirection: 'column' }}>
      {/* Encabezado */}
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 2,
          cursor: 'pointer',
          p: 2,
          borderRadius: 1
        }}
        onClick={() => setShowAdjuntos(!showAdjuntos)}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Historial de Avances ({avancesValidos.length})
          {loadingAvances && <CircularProgress size={20} sx={{ ml: 2 }} />}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ mr: 1, color: 'text.secondary' }}>
            {showAdjuntos ? 'Ocultar adjuntos' : 'Mostrar adjuntos'}
          </Typography>
          <IconButton>
            {showAdjuntos ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      {/* Barra de búsqueda */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          sx={{ backgroundColor: 'white'}}
          placeholder="Buscar en avances..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      
      {/* Contenedor con scroll */}
      <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
        {loadingAvances ? (
          <Box display="flex" justifyContent="center" sx={{ py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>Cargando avances...</Typography>
          </Box>
        ) : filteredAvances.length === 0 ? (
          <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center', py: 3 }}>
            {searchTerm ? 'No se encontraron avances que coincidan con la búsqueda' : 'No hay avances registrados para esta demanda.'}
          </Typography>
        ) : (
          <List>
            {filteredAvances.map((avance) => {
              if (!avance) return null;
              
              const adjuntosRelacionados = getAdjuntosRelacionados(avance);
              const esAvanceCierre = avance.es_cierre || avance.tipo_avance === 'CIERRE';
              
              // Validaciones seguras para los datos del avance
              const descripcion = avance.descripcionavance || 'Sin descripción';
              const fecha = avance.fechaavance ? new Date(avance.fechaavance).toLocaleString('es-HN') : 'Fecha no disponible';
              const avanceId = avance.id || Math.random().toString();

              return (
                <React.Fragment key={avanceId}>
                  <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: esAvanceCierre ? 'error.main' : 'primary.main' }}>
                        {esAvanceCierre ? (
                          <LockIcon />
                        ) : (
                          <Description />
                        )}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                              {fecha}
                            </Typography>
                            {esAvanceCierre && (
                              <Chip 
                                label="CIERRE" 
                                color="error" 
                                size="small" 
                                sx={{ ml: 1, fontWeight: 'bold' }} 
                              />
                            )}
                          </Box>
                          <Typography variant="caption" color="textSecondary">
                            Usuario: {getNombreUsuario(avance)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" color="text.primary" paragraph sx={{ mt: 1 }}>
                            {descripcion}
                          </Typography>
                          {esAvanceCierre && (
                            <Box sx={{ 
                              backgroundColor: '#ffeeee', 
                              p: 1, 
                              borderRadius: 1,
                              mt: 1,
                              borderLeft: '3px solid #f44336'
                            }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                Motivo de cierre:
                              </Typography>
                              <Typography variant="body2">
                                {descripcion.replace('Cierre de caso - Motivo: ', '')}
                              </Typography>
                            </Box>
                          )}
                          {showAdjuntos && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Adjuntos ({Array.isArray(adjuntosRelacionados) ? adjuntosRelacionados.length : 0})
                              </Typography>
                              {Array.isArray(adjuntosRelacionados) && adjuntosRelacionados.length > 0 ? (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                  {adjuntosRelacionados.map(adjunto => {
                                    if (!adjunto) return null;
                                    
                                    const adjuntoId = adjunto.id;
                                    const nombreArchivo = `${adjunto.nombre}`;
                                    
                                    return (
                                      <Chip
                                        key={adjuntoId}
                                        icon={<Description />}
                                        label={nombreArchivo}
                                        onClick={() => handlePreview(adjuntoId, nombreArchivo)}
                                        onDelete={() => handleDescargarAdjunto(adjuntoId, nombreArchivo)}
                                        deleteIcon={
                                          descargandoId === adjuntoId ? 
                                            <CircularProgress size={20} /> : 
                                            <Download />
                                        }
                                        variant="outlined"
                                        sx={{ cursor: 'pointer' }}
                                        disabled={descargandoId === adjuntoId}
                                      />
                                    );
                                  })}
                                </Box>
                              ) : (
                                <Typography variant="caption" color="textSecondary">
                                  No hay archivos adjuntos para este avance.
                                </Typography>
                              )}
                            </Box>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                  <Divider component="li" sx={{ my: 1 }} />
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Box>
    </Paper>
  );
};

export default HistorialAvances;